import * as React from "react";
import { DismissableLayerBranch } from "@radix-ui/react-dismissable-layer";

import { ToastCard } from "@/components/ui/toast";
import { useDeviceTier } from "@/hooks/useDeviceTier";
import { getToastAnchors, subscribeToastAnchors, toastPlacement, type ToastPlacement } from "@/lib/toastAnchors";
import { pauseToasts, resumeToasts, useToastList } from "@/lib/toastStore";

/** The toast column's widest; narrower screens keep a 16px gutter each side. */
const TOAST_MAX_WIDTH = 420;
const TOAST_GUTTER = 16;
/** Beside a docked bar (the cart on a landscape tablet) the column may narrow to this, else it goes above the bar. */
const TOAST_MIN_WIDTH = 320;
/** Space between the stack and a bar it sits above. */
const GAP_ABOVE_BAR = 8;
const NO_BARS: ToastPlacement = { bottom: 0, right: 0 };
/** Toasts shown at once; older ones fold into "+N more". Fewer on a phone, so the stack leaves the screen usable. */
const VISIBLE_TOASTS = 4;
const VISIBLE_TOASTS_PHONE = 3;

/**
 * Where the stack goes: clear of every bar it would cover (useToastAnchor,
 * toastPlacement). Measured while toasts are up, again when a bar appears,
 * resizes or finishes animating in (the cart sheet), or the window resizes.
 */
function usePlacement(active: boolean, toasts: unknown): ToastPlacement {
  const [placement, setPlacement] = React.useState<ToastPlacement>(NO_BARS);
  const measure = React.useCallback(() => {
    const next = active
      ? toastPlacement(window.innerWidth, window.innerHeight, {
          maxWidth: TOAST_MAX_WIDTH,
          gutter: TOAST_GUTTER,
          minWidth: TOAST_MIN_WIDTH,
        })
      : NO_BARS;
    // Always leave room for at least one toast.
    const bottom = Math.min(next.bottom, Math.max(0, window.innerHeight - 160));
    setPlacement((now) => (now.bottom === bottom && now.right === next.right ? now : { bottom, right: next.right }));
  }, [active]);

  React.useLayoutEffect(() => {
    measure();
  }, [measure, toasts]);

  React.useEffect(() => {
    if (!active) return;
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(() => measure()) : null;
    const observeAnchors = () => {
      observer?.disconnect();
      for (const anchor of getToastAnchors()) observer?.observe(anchor);
      measure();
    };
    observeAnchors();
    const unsubscribe = subscribeToastAnchors(observeAnchors);
    window.addEventListener("resize", measure);
    document.addEventListener("animationend", measure, true);
    document.addEventListener("transitionend", measure, true);
    return () => {
      unsubscribe();
      observer?.disconnect();
      window.removeEventListener("resize", measure);
      document.removeEventListener("animationend", measure, true);
      document.removeEventListener("transitionend", measure, true);
    };
  }, [active, measure]);

  return placement;
}

/**
 * Every staff-app toast, both APIs (src/lib/toastStore.ts): bottom centre,
 * newest at the bottom, nearest the edge. Up to four show (three on a phone);
 * older ones fold into "+N more". Above the safe-area inset, and clear of the
 * Orders cart bar and cart actions where those are on screen: above them, or
 * beside the cart where it is docked on the right (a landscape tablet).
 *
 * Pointer over the stack, or focus in it: every countdown holds, so nothing
 * moves under the pointer; they run on when it leaves. F8 moves focus to the
 * stack. It is a branch of any open dialog or sheet: tapping a toast does not
 * close what is underneath.
 */
export function Toaster() {
  const toasts = useToastList();
  const limit = useDeviceTier() === "phone" ? VISIBLE_TOASTS_PHONE : VISIBLE_TOASTS;
  const [expanded, setExpanded] = React.useState(false);
  const columnRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLOListElement>(null);

  const open = toasts.filter((t) => t.open);
  const overflow = Math.max(0, open.length - limit);
  const showAll = expanded && overflow > 0;
  const newest = new Set(open.slice(-limit).map((t) => t.id));
  // A closing toast plays its exit only where it was showing (not one folded into "+N more").
  const shownBefore = React.useRef(new Set<string>());
  const shown = toasts.filter((t) => (t.open ? showAll || newest.has(t.id) : shownBefore.current.has(t.id)));
  React.useEffect(() => {
    shownBefore.current = new Set(shown.map((t) => t.id));
  });

  React.useEffect(() => {
    if (overflow === 0) setExpanded(false);
  }, [overflow]);

  // Focus or the pointer can leave with a toast (its X removed it): don't stay paused.
  React.useEffect(() => {
    const column = columnRef.current;
    if (!column || !column.contains(document.activeElement)) resumeToasts("focus");
    if (open.length === 0) resumeToasts("hover");
  }, [toasts, open.length]);

  React.useEffect(() => {
    const onVisibility = () => (document.hidden ? pauseToasts("hidden") : resumeToasts("hidden"));
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      for (const reason of ["hidden", "hover", "focus"]) resumeToasts(reason);
    };
  }, []);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "F8" || event.altKey || event.ctrlKey || event.metaKey) return;
      const list = listRef.current;
      if (!list || list.childElementCount === 0) return;
      event.preventDefault();
      list.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const placement = usePlacement(open.length > 0, toasts);
  const bottom = placement.bottom > 0 ? placement.bottom + GAP_ABOVE_BAR : null;

  return (
    <DismissableLayerBranch asChild>
      <section
        aria-label="Notifications (F8)"
        data-toaster=""
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex justify-center px-4 pb-[max(1rem,calc(env(safe-area-inset-bottom)+0.5rem))]"
        style={{
          ...(bottom !== null && { paddingBottom: bottom }),
          ...(placement.right > 0 && { paddingRight: placement.right + TOAST_GUTTER }),
        }}
      >
        <div
          ref={columnRef}
          className="pointer-events-auto flex w-full max-w-[420px] flex-col items-stretch gap-2"
          onPointerEnter={() => pauseToasts("hover")}
          onPointerLeave={() => resumeToasts("hover")}
          onFocus={() => pauseToasts("focus")}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) resumeToasts("focus");
          }}
        >
          {overflow > 0 && (
            <button
              type="button"
              aria-expanded={showAll}
              onClick={() => setExpanded((on) => !on)}
              className="touch-hit press self-center rounded-full bg-ink px-3 py-1.5 text-meta font-semibold text-white elev-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {showAll ? "Show fewer" : `+${overflow} more`}
            </button>
          )}
          {/* Not a live region itself: each toast is one (role status or alert), and nested ones double-announce. */}
          <ol
            ref={listRef}
            tabIndex={-1}
            className={
              showAll
                ? // Room inside the scroller for the toasts' shadow, so it isn't cut off in a box.
                  "-mx-4 -mb-4 -mt-1 flex flex-col gap-2 overflow-y-auto overscroll-contain px-4 pb-4 pt-1 outline-none"
                : "flex flex-col gap-2 outline-none"
            }
            style={showAll ? { maxHeight: `calc(100dvh - ${bottom ?? 16}px - 5rem)` } : undefined}
          >
            {shown.map((toast) => (
              <ToastCard key={toast.id} toast={toast} />
            ))}
          </ol>
        </div>
      </section>
    </DismissableLayerBranch>
  );
}
